// Lecture du trajet (replay) d'une carte « trace », à l'écran seulement, sur le modèle de
// l'historique de la plateforme GPS Digitrans (module géoloc) : modèle de temps, état du véhicule
// à chaque instant et lecteur. Fonctions pures, sans React ni MapLibre (testées par node --test) ;
// le lecteur reçoit son horloge et son ordonnanceur d'images (requestAnimationFrame par défaut).
//
// Temps : heures réelles des positions (heure d'Abidjan = UTC, comme le reste de l'interface),
// mises à l'échelle par la vitesse de lecture. À ×1, le trajet entier dure 45 s (REPLAY_TARGET_MS) ;
// moins pour une trace de moins de 31 positions (1,5 s par segment), jamais moins de 30 s.
// Un arrêt long (positions groupées dans 40 m pendant 3 min au moins) et une absence de position
// de 10 min au moins sont joués en 1,5 s au plus à ×1 ; un arrêt est annoncé (« Arrêt de 25 min »).
// Sans heures lisibles : chaque segment dure le même temps. Vitesse affichée : celle enregistrée à
// la dernière position atteinte, jamais calculée. Cap du véhicule : celui des données s'il existe
// (mesures.cap), sinon la direction entre deux positions ; lissé, il tourne par le plus court
// chemin. Les exports (image, GeoJSON, CSV) et les documents n'en dépendent pas : trace entière,
// sans véhicule.

export const REPLAY_TARGET_MS = 45000;
const REPLAY_MIN_MS = 30000;
const SEGMENT_TARGET_MS = 1500;
export const STOP_MIN_MS = 3 * 60000;
export const GAP_MIN_MS = 10 * 60000;
export const STOP_PLAY_MS = 1500;
const STOP_RADIUS_M = 40;
// Direction lue sur un déplacement d'au moins 5 m (en deçà : bruit du GPS, cap précédent gardé)
const HEADING_MIN_M = 5;
// Cap des données ignoré à l'arrêt (le GPS n'en donne pas de fiable sous 3 km/h)
const COURSE_MIN_KMH = 3;
// Deux positions de même heure : 1 s entre elles (jamais de saut instantané)
const MIN_SEGMENT_REAL_MS = 1000;
/** Vitesses proposées, relatives à ×1 (trajet entier en environ 45 s). */
export const REPLAY_RATES = [0.5, 1, 2, 4, 8];
/** Mouvement réduit : le véhicule avance de position en position, une fois toutes les 450 ms au plus. */
export const STEP_MODE_MS = 450;
/** Badge d'un arrêt gardé 1,2 s au moins pendant la lecture (lisible aux grandes vitesses). */
export const STOP_BADGE_MIN_MS = 1200;
// Image retardée (onglet en arrière-plan) : jamais plus de 100 ms d'avance d'un coup
const MAX_FRAME_MS = 100;
const HEADING_TAU_MS = 110;

const EARTH_RADIUS_M = 6371008.8;
const rad = (deg) => (deg * Math.PI) / 180;
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// « 2026-10-11 06:12:00 », « 2026-10-11T06:12 », avec ou sans fuseau
const TIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:[.,]\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i;

/**
 * Heure d'une position → instant (ms) : « AAAA-MM-JJ HH:MM:SS » = heure d'Abidjan (UTC) ; avec
 * fuseau (« Z », « +01:00 ») : instant exact ; null si illisible.
 */
export function parseTraceTime(value) {
  const m = typeof value === 'string' ? TIME_RE.exec(value.trim()) : null;
  if (!m) return null;
  const local = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? 0));
  if (!m[7] || m[7].toUpperCase() === 'Z') return local;
  const [, sign, hh, mm] = /^([+-])(\d{2}):?(\d{2})$/.exec(m[7]);
  return local - (sign === '+' ? 1 : -1) * (Number(hh) * 60 + Number(mm)) * 60000;
}

/** Date et heure d'un instant à l'heure d'Abidjan (UTC) : {date « 11/10/2026 », time « 06:12:00 »}, ou null. */
export function formatReplayClock(ms) {
  if (!isNum(ms)) return null;
  const d = new Date(Math.round(ms));
  const p = (v) => String(v).padStart(2, '0');
  return { date: `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`, time: `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}` };
}

/** Distance (m) entre deux positions {lng, lat}. */
export function distanceM(a, b) {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Direction de a vers b telle que la carte la dessine (0 = nord, sens horaire, degrés). */
export function bearingDeg(a, b) {
  const dx = (b.lng - a.lng) * Math.cos(rad((a.lat + b.lat) / 2));
  return ((Math.atan2(dx, b.lat - a.lat) * 180) / Math.PI + 360) % 360;
}

/** Écart signé le plus court d'un cap à un autre, dans ]-180, 180]. */
export function angleDelta(from, to) {
  const d = ((((to - from) % 360) + 540) % 360) - 180;
  return d === -180 ? 180 : d;
}

/** Cap lissé vers `target` (amorti exponentiel, indépendant de la cadence des images), dans [0, 360[. */
export function smoothHeading(current, target, dtMs, tauMs = HEADING_TAU_MS) {
  if (!isNum(current)) return target;
  const k = 1 - Math.exp(-Math.max(0, dtMs) / tauMs);
  return (current + angleDelta(current, target) * k + 360) % 360;
}

/** « Arrêt de 25 min », « Arrêt de 1 h 10 », « Arrêt de 2 h ». */
export function stopLabel(ms) {
  const min = Math.max(1, Math.round(ms / 60000));
  if (min < 60) return `Arrêt de ${min} min`;
  const rest = min % 60;
  return `Arrêt de ${Math.floor(min / 60)} h${rest ? ` ${String(rest).padStart(2, '0')}` : ''}`;
}

// Heures connues → instants de toutes les positions : rang interpolé entre deux heures connues,
// celle de la plus proche avant la première et après la dernière, jamais en arrière ; null sans
// deux heures distinctes
function fillTimes(list) {
  const known = [];
  list.forEach((ms, k) => {
    if (ms !== null) known.push(k);
  });
  if (known.length < 2 || list[known.at(-1)] <= list[known[0]]) return null;
  const out = new Float64Array(list.length);
  for (let k = 0; k <= known[0]; k += 1) out[k] = list[known[0]];
  for (let j = 1; j < known.length; j += 1) {
    const [a, b] = [known[j - 1], known[j]];
    for (let k = a + 1; k <= b; k += 1) out[k] = list[a] + ((list[b] - list[a]) * (k - a)) / (b - a);
  }
  for (let k = known.at(-1) + 1; k < list.length; k += 1) out[k] = list[known.at(-1)];
  for (let k = 1; k < out.length; k += 1) if (out[k] < out[k - 1]) out[k] = out[k - 1];
  return out;
}

// Vitesse de lecture à ×1 (ms réelles par ms de lecture) qui joue le trajet en `target` ms : la durée
// de lecture décroît avec elle (arrêts plafonnés à STOP_PLAY_MS), recherche par dichotomie
function solveRate(moving, runs, target) {
  const playback = (rate) => moving / rate + runs.reduce((sum, ms) => sum + Math.min(ms / rate, STOP_PLAY_MS), 0);
  if (moving <= 0) return Math.max(1, ...runs.map((ms) => ms / STOP_PLAY_MS));
  let lo = 1e-3;
  let hi = 1e9;
  for (let i = 0; i < 90; i += 1) {
    const mid = Math.sqrt(lo * hi);
    if (playback(mid) > target) lo = mid;
    else hi = mid;
  }
  return Math.sqrt(lo * hi);
}

// Arrêts et absences de position : [{from, to, ms, stop}] (positions from → to, durée réelle)
function findRuns(vertices, times, real) {
  const runs = [];
  const segs = vertices.length - 1;
  let a = 0;
  while (a < segs) {
    let b = a;
    while (b + 1 < vertices.length && distanceM(vertices[a], vertices[b + 1]) <= STOP_RADIUS_M) b += 1;
    if (b > a && times[b] - times[a] >= STOP_MIN_MS) {
      const prev = runs.at(-1);
      // Deux groupes voisins (le véhicule s'est déplacé de quelques mètres sur un parking) : un arrêt
      if (prev?.stop && prev.to === a) prev.to = b;
      else runs.push({ from: a, to: b, stop: true });
      a = b;
    } else {
      if (real[a] >= GAP_MIN_MS) runs.push({ from: a, to: a + 1, stop: false });
      a += 1;
    }
  }
  for (const run of runs) run.ms = times[run.to] - times[run.from];
  return runs;
}

/**
 * Modèle de temps du replay d'une trace normalisée (normalizeTrace), ou null sous 2 positions :
 *   {vertices, n, vt (instant de lecture à ×1 de chaque position, ms), total, times (instants réels,
 *   ms, ou null : heures absentes, segments de même durée), timed, realPerMs (ms réelles par ms de
 *   lecture à ×1, ou null), stops [{from, to, ms, start, end, label}], stopOf (arrêt de chaque
 *   segment, -1 sinon), headings (direction de chaque segment), courses (cap des données, ou null),
 *   simplified}
 */
export function buildReplayTimeline(trace) {
  const vertices = Array.isArray(trace?.vertices) ? trace.vertices : [];
  const n = vertices.length;
  if (n < 2) return null;
  const segs = n - 1;
  const target = clamp(segs * SEGMENT_TARGET_MS, REPLAY_MIN_MS, REPLAY_TARGET_MS);
  const times = fillTimes(vertices.map((v) => parseTraceTime(v.time)));
  const vd = new Float64Array(segs);
  const stopOf = new Int32Array(segs).fill(-1);
  const stops = [];
  let realPerMs = null;
  if (times) {
    const real = Float64Array.from({ length: segs }, (_, i) => Math.max(MIN_SEGMENT_REAL_MS, times[i + 1] - times[i]));
    const runs = findRuns(vertices, times, real);
    const inRun = new Uint8Array(segs);
    const runReal = runs.map((run) => {
      let ms = 0;
      for (let i = run.from; i < run.to; i += 1) {
        inRun[i] = 1;
        ms += real[i];
      }
      return ms;
    });
    let moving = 0;
    for (let i = 0; i < segs; i += 1) if (!inRun[i]) moving += real[i];
    realPerMs = solveRate(moving, runReal, target);
    for (let i = 0; i < segs; i += 1) vd[i] = real[i] / realPerMs;
    runs.forEach((run, j) => {
      const play = Math.min(runReal[j] / realPerMs, STOP_PLAY_MS);
      for (let i = run.from; i < run.to; i += 1) {
        vd[i] = (play * real[i]) / runReal[j];
        if (run.stop) stopOf[i] = stops.length;
      }
      if (run.stop) stops.push({ from: run.from, to: run.to, ms: run.ms, label: stopLabel(run.ms) });
    });
  } else {
    vd.fill(target / segs);
  }
  const vt = new Float64Array(n);
  for (let i = 0; i < segs; i += 1) vt[i + 1] = vt[i] + vd[i];
  for (const stop of stops) Object.assign(stop, { start: vt[stop.from], end: vt[stop.to] });

  // Direction de chaque segment : déplacements de 5 m au moins, hors arrêts ; sinon la précédente
  // (la suivante en tête de trace), le nord si le véhicule n'a jamais bougé
  const headings = new Float64Array(segs).fill(Number.NaN);
  for (let i = 0; i < segs; i += 1) {
    if (stopOf[i] < 0 && distanceM(vertices[i], vertices[i + 1]) >= HEADING_MIN_M) headings[i] = bearingDeg(vertices[i], vertices[i + 1]);
  }
  for (let i = 1; i < segs; i += 1) if (Number.isNaN(headings[i])) headings[i] = headings[i - 1];
  for (let i = segs - 2; i >= 0; i -= 1) if (Number.isNaN(headings[i])) headings[i] = headings[i + 1];
  if (Number.isNaN(headings[0])) headings.fill(0);
  const courses = vertices.some((v) => isNum(v.course))
    ? Float64Array.from(vertices, (v) => (isNum(v.course) && !(isNum(v.speed) && v.speed < COURSE_MIN_KMH) ? ((v.course % 360) + 360) % 360 : Number.NaN))
    : null;

  return { vertices, n, vt, total: vt[segs], times, timed: !!times, realPerMs, stops, stopOf, headings, courses, simplified: trace.simplified === true };
}

// Segment joué à l'instant t : vt[k] ≤ t < vt[k + 1] (le dernier à la fin)
function segmentAt(vt, t) {
  let lo = 0;
  let hi = vt.length - 2;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (vt[mid] <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

// Cap au rang k, fraction f du segment : celui des données (interpolé par le plus court chemin),
// sinon la direction du segment
function headingAt(tl, k, f) {
  const c = tl.courses;
  if (c && tl.stopOf[k] < 0 && !Number.isNaN(c[k])) {
    return Number.isNaN(c[k + 1]) ? c[k] : (c[k] + angleDelta(c[k], c[k + 1]) * f + 360) % 360;
  }
  return tl.headings[k];
}

/**
 * État du véhicule à l'instant de lecture `t` (ms à ×1) : {t, progress (0-1), segment, fraction,
 * reached (dernière position atteinte), lng, lat, heading, time (instant réel, ou null), speed
 * (enregistrée à la dernière position atteinte, ou null), stop (arrêt en cours, ou null)}.
 * `step` (mouvement réduit) : le véhicule est posé sur la dernière position atteinte, à son heure.
 */
export function replayStateAt(tl, t, { step = false } = {}) {
  const at = clamp(isNum(t) ? t : 0, 0, tl.total);
  const k = segmentAt(tl.vt, at);
  const span = tl.vt[k + 1] - tl.vt[k];
  const f = span > 0 ? Math.min(1, (at - tl.vt[k]) / span) : 1;
  const reached = f >= 1 ? k + 1 : k;
  const stopIndex = tl.stopOf[k];
  const base = {
    t: at,
    progress: tl.total > 0 ? at / tl.total : 1,
    segment: k,
    reached,
    speed: isNum(tl.vertices[reached].speed) ? tl.vertices[reached].speed : null,
    stop: stopIndex >= 0 ? tl.stops[stopIndex] : null,
  };
  if (step) {
    // Cap du segment qui part de la position atteinte (de celui qui arrive à la dernière)
    const v = tl.vertices[reached];
    const s = Math.min(reached, tl.n - 2);
    return { ...base, fraction: reached > k ? 1 : 0, lng: v.lng, lat: v.lat, heading: headingAt(tl, s, reached > s ? 1 : 0), time: tl.times ? tl.times[reached] : null };
  }
  const a = tl.vertices[k];
  const b = tl.vertices[k + 1];
  return {
    ...base,
    fraction: f,
    lng: a.lng + (b.lng - a.lng) * f,
    lat: a.lat + (b.lat - a.lat) * f,
    heading: headingAt(tl, k, f),
    time: tl.times ? tl.times[k] + (tl.times[k + 1] - tl.times[k]) * f : null,
  };
}

/** Rang de la position la plus proche de {lng, lat} (clic sur la ligne de la trace), ou null. */
export function nearestVertexIndex(vertices, point) {
  const lng = point?.lng;
  const lat = point?.lat;
  if (!isNum(lng) || !isNum(lat) || !vertices?.length) return null;
  const k = Math.cos(rad(lat));
  let best = null;
  let bestD = Infinity;
  vertices.forEach((v, i) => {
    const d = ((v.lng - lng) * k) ** 2 + (v.lat - lat) ** 2;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

/** « Position 12 sur 240 » (« Sommet » sur une trace simplifiée) : dernière position atteinte. */
export function replayPositionText(tl, state) {
  return `${tl.simplified ? 'Sommet' : 'Position'} ${state.reached + 1} sur ${tl.n}`;
}

/** « ×2 · 20 min/s » : vitesse de lecture et trajet parcouru par seconde (1 min/s au moins) ; « ×2 » sans heures. */
export function replayRateText(tl, rate) {
  const label = `×${String(rate).replace('.', ',')}`;
  const perSecond = tl?.realPerMs ? tl.realPerMs * rate : 0;
  if (perSecond < 60) return label;
  if (perSecond < 3600) return `${label} · ${Math.round(perSecond / 60)} min/s`;
  return `${label} · ${String(Math.round(perSecond / 360) / 10).replace('.', ',')} h/s`;
}

/** Segments de la trace (un par paire de positions, `id` = rang) : la partie jouée s'éclaire par feature-state. */
export function replaySegmentsGeoJSON(vertices) {
  const features = [];
  for (let i = 0; i + 1 < (vertices?.length ?? 0); i += 1) {
    features.push({ type: 'Feature', id: i, geometry: { type: 'LineString', coordinates: [[vertices[i].lng, vertices[i].lat], [vertices[i + 1].lng, vertices[i + 1].lat]] }, properties: {} });
  }
  return { type: 'FeatureCollection', features };
}

const EMPTY = { type: 'FeatureCollection', features: [] };
/** Partie du segment en cours déjà parcourue (de sa première position au véhicule), vide sinon. */
export function replayHeadGeoJSON(tl, frame) {
  if (!frame || frame.fraction <= 0 || frame.fraction >= 1 || frame.stepMode) return EMPTY;
  const a = tl.vertices[frame.segment];
  return { type: 'Feature', geometry: { type: 'LineString', coordinates: [[a.lng, a.lat], [frame.lng, frame.lat]] }, properties: {} };
}

const defaultNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const defaultRequest = (cb) => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(cb) : setTimeout(cb, 16));
const defaultCancel = (id) => (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame(id) : clearTimeout(id));

/**
 * Lecteur du trajet : instant de lecture (ms à ×1), lecture ou pause, vitesse, suivi du véhicule.
 * Une image par requestAnimationFrame pendant la lecture, la position en mouvement réduit. Deux
 * abonnements : `onFrame` (état du véhicule à chaque image : carte et barre de lecture, sans rendu
 * React) et `subscribe` / `getSnapshot` (états lents pour useSyncExternalStore : lecture, fin,
 * vitesse, suivi, arrêt affiché, annonce du lecteur d'écran). `hold` suspend la lecture (curseur
 * tenu, image exportée) ; `prepareCapture` y ajoute la trace entière pour l'export de l'image.
 */
export function createReplayPlayer(timeline, { now = defaultNow, requestFrame = defaultRequest, cancelFrame = defaultCancel } = {}) {
  const tl = timeline;
  let t = 0;
  let playing = false;
  let stepMode = false;
  let holds = 0;
  let frameId = null;
  let last = 0;
  let lastEmit = -Infinity;
  let heading = null;
  let frame = null;
  let shownStop = null;
  let shownAt = 0;
  let captureHook = null;
  let said = 0;
  const frameListeners = new Set();
  const listeners = new Set();
  let snapshot = { playing: false, ended: false, engaged: false, rate: 1, follow: false, stepMode: false, stop: null, message: '' };

  const update = (patch) => {
    if (Object.keys(patch).every((key) => snapshot[key] === patch[key])) return;
    snapshot = { ...snapshot, ...patch };
    for (const listener of [...listeners]) listener();
  };
  // Même annonce deux fois de suite : une espace insécable la rend différente (relue)
  const announce = (text) => {
    said += 1;
    update({ message: said % 2 ? text : `${text} ` });
  };
  const stateNow = () => replayStateAt(tl, t, { step: stepMode });

  const emit = (dt = 0, snap = false) => {
    const state = stateNow();
    heading = snap || stepMode || heading === null ? state.heading : smoothHeading(heading, state.heading, dt);
    frame = { ...state, heading, dt, snap, playing, engaged: snapshot.engaged, follow: snapshot.follow, stepMode };
    for (const listener of [...frameListeners]) listener(frame);
    const stop = snapshot.engaged ? state.stop : null;
    if (stop === shownStop) return;
    const at = now();
    if (stop) {
      shownStop = stop;
      shownAt = at;
      update({ stop: stop.label });
      if (playing) announce(stop.label);
    } else if (!playing || at - shownAt >= STOP_BADGE_MIN_MS) {
      shownStop = null;
      update({ stop: null });
    }
  };

  const cancel = () => {
    if (frameId !== null) cancelFrame(frameId);
    frameId = null;
  };
  const schedule = () => {
    if (frameId === null && playing && holds === 0) frameId = requestFrame(tick);
  };
  function tick() {
    frameId = null;
    if (!playing || holds > 0) return;
    const at = now();
    const dt = clamp(at - last, 0, MAX_FRAME_MS);
    last = at;
    t = Math.min(tl.total, t + dt * snapshot.rate);
    const ended = t >= tl.total;
    if (ended) playing = false;
    if (ended || !stepMode || at - lastEmit >= STEP_MODE_MS) {
      lastEmit = at;
      emit(dt);
    }
    if (ended) {
      update({ playing: false, ended: true });
      announce('Fin du trajet');
    } else schedule();
  }

  const clockText = () => {
    const state = stateNow();
    const clock = tl.timed ? formatReplayClock(state.time) : null;
    return clock ? `${clock.date} ${clock.time}` : replayPositionText(tl, state);
  };
  const play = (message = 'Lecture du trajet') => {
    if (playing) return;
    if (t >= tl.total) {
      t = 0;
      heading = null;
    }
    playing = true;
    last = now();
    lastEmit = -Infinity;
    update({ playing: true, ended: false, engaged: true });
    announce(message);
    emit(0, heading === null);
    schedule();
  };
  const pause = () => {
    if (!playing) return;
    playing = false;
    cancel();
    emit(0);
    update({ playing: false });
    announce(`Pause, ${clockText()}`);
  };
  const seek = (value) => {
    t = clamp(isNum(value) ? value : 0, 0, tl.total);
    last = now();
    update({ engaged: true, ended: !playing && t >= tl.total });
    emit(0, true);
  };
  const seekToVertex = (index) => seek(tl.vt[clamp(Math.round(index) || 0, 0, tl.n - 1)]);
  const hold = () => {
    holds += 1;
    cancel();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      holds = Math.max(0, holds - 1);
      if (holds === 0 && playing) {
        last = now();
        schedule();
      }
    };
  };

  return {
    timeline: tl,
    play: () => play(),
    pause,
    toggle: () => (playing ? pause() : play()),
    seek,
    seekToVertex,
    /** Lecture à partir de la position `index` (« Rejouer à partir d'ici »). */
    playFrom(index) {
      seekToVertex(index);
      if (playing) return;
      play(`Lecture à partir de ${clockText()}`);
    },
    /** Position précédente (direction < 0) ou suivante. */
    stepBy(direction) {
      let k;
      if (direction > 0) {
        k = 0;
        while (k < tl.n - 1 && tl.vt[k] <= t + 1e-6) k += 1;
      } else {
        k = tl.n - 1;
        while (k > 0 && tl.vt[k] >= t - 1e-6) k -= 1;
      }
      seek(tl.vt[k]);
    },
    /** Saut d'une fraction du trajet (± 0,1 : un dixième). */
    jumpBy: (fraction) => seek(t + fraction * tl.total),
    setRate(rate) {
      if (REPLAY_RATES.includes(rate)) update({ rate });
    },
    /** Suivi du véhicule (la carte reste centrée sur lui) ; activé, il fait apparaître le véhicule. */
    setFollow(on) {
      update(on ? { follow: true, engaged: true } : { follow: false });
      announce(on ? 'Suivi du véhicule activé' : 'Suivi du véhicule désactivé');
    },
    setStepMode(on) {
      stepMode = !!on;
      update({ stepMode });
      emit(0, true);
    },
    hold,
    setCaptureHook(fn) {
      captureHook = fn;
      return () => {
        if (captureHook === fn) captureHook = null;
      };
    },
    /** Image exportée : lecture suspendue, trace entière ; renvoie la fonction qui rétablit le replay. */
    prepareCapture() {
      const release = hold();
      let restore = null;
      try {
        restore = captureHook ? captureHook() : null;
      } catch {
        restore = null;
      }
      return () => {
        try {
          restore?.();
        } finally {
          release();
        }
      };
    },
    onFrame(listener) {
      frameListeners.add(listener);
      return () => frameListeners.delete(listener);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
    getFrame() {
      if (!frame) {
        const state = stateNow();
        heading = state.heading;
        frame = { ...state, dt: 0, snap: true, playing, engaged: snapshot.engaged, follow: snapshot.follow, stepMode };
      }
      return frame;
    },
    /** Démontage : lecture arrêtée (le lecteur reste utilisable, double montage de React en développement). */
    dispose() {
      playing = false;
      holds = 0;
      cancel();
      update({ playing: false });
    },
  };
}
